import { Box, Stack } from '@mui/material';

import CalendarField from '../../components/CalendarField/CalendarField';
import EmploymentSubtitle from '../../components/EmploymentSubtitle/EmploymentSubtitle';
import OccupationField from '../../components/OccupationField/OccupationField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useFieldToggle from '../../hooks/useFieldToggle';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';

const EmploymentPage = () => {
    useComponentTracker('EmploymentPage');

    const dispatch = useAppDispatch();
    const { totalSections, totalAboutYouPages } = usePagesConfig();
    const { currentPage } = usePagesContext();
    const translate = useTranslation();
    const defaultPagesAction = useDefaultPagesAction();

    const employmentType = useAppSelector(selectFieldValue)('applicantEmploymentType');

    const requiredFields = ['applicantEmploymentType'];
    if (employmentType) {
        requiredFields.push('applicantEmployedSince');
    }
    const validationErrors = useValidation(requiredFields);

    useFieldToggle(!!employmentType, ['applicantEmployedSince']);

    useSetBannerType('bannerOne');

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next' && validationErrors.length) {
            scrollToFieldWithError(validationErrors);
            validationErrors.forEach((validationError) => {
                dispatch(addOrUpdateFormField(validationError));
            });
            return false;
        }

        return defaultPagesAction(action);
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.employment.title')} />
            <ShowDetailsModal
                label={translate('dialog.know-more.label')}
                dialogTitle={translate('dialog.know-more.label')}
                dialogContent={translate('dialog.employment.content')}
            />
            <Stack spacing={2}>
                <Box>
                    <OccupationField
                        label={translate('field.applicant-employment-type.label')}
                        name={'applicantEmploymentType'}
                    />
                </Box>
            </Stack>
            {employmentType && (
                <Stack spacing={2} sx={{ mt: 2}}>
                    <EmploymentSubtitle employmentType={employmentType as string} />
                    <CalendarField
                        name={'applicantEmployedSince'}
                        label={translate('field.applicant-employment-date.label')}
                    />
                </Stack>
            )}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};
export default EmploymentPage;
